import { debounce } from 'lodash-es';
import { HawkSearchComponents, SearchFieldComponentConfig, SearchFieldComponentModel, SearchResponse } from '@models';
import { autocompleteService, searchService } from '@services';
import { BaseComponent } from '../../base.component';
import { AutocompleteComponent } from '../autocomplete/autocomplete.component';
import defaultHtml from './search-field.component.hbs';

export class SearchFieldComponent extends BaseComponent<SearchFieldComponentConfig, SearchResponse, SearchFieldComponentModel> {
    protected override componentName: keyof HawkSearchComponents = 'search-field';
    protected override defaultHtml = defaultHtml;
    protected override bindFromEvent = true;

    private clickEventHandler!: (event: MouseEvent) => void;

    override connectedCallback(): void {
        super.connectedCallback();

        this.clickEventHandler = (event: Event): void => {
            const element = event.target as HTMLElement;
            const searchElement = !!element.closest('hawksearch-search-field');

            if (!searchElement) {
                this.toggleAutocomplete(false);
            }
        };

        this.rootElement.ownerDocument!.addEventListener('click', this.clickEventHandler);
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();

        this.rootElement.ownerDocument!.removeEventListener('click', this.connectedCallback);
    }

    protected override getContentModel(): SearchFieldComponentModel {
        return {
            query: this.data?.query,
            strings: {
                placeholder: this.configuration?.strings?.placeholder ?? 'Enter keywords'
            }
        };
    }

    protected bindChildElements(): void {
        const enterKeys = ['Enter', 'NumpadEnter'];

        this.rootElement.querySelectorAll('[hawksearch-input]').forEach((e) => {
            e.addEventListener('keyup', ((event: KeyboardEvent): void => {
                if (!enterKeys.includes(event.code)) {
                    return;
                }

                const element = event.currentTarget as HTMLInputElement;
                const query = element.value.trim();

                this.toggleAutocomplete(false);

                searchService.query(query);
            }) as EventListener);

            const onKeyUp = (event: KeyboardEvent): void => {
                if (enterKeys.includes(event.code)) {
                    return;
                }

                const element = e as HTMLInputElement; // Not using event.currentTarget as that gets lost when using lodash debounce
                const query = element.value.trim();

                this.toggleAutocomplete(!!query);

                if (query) {
                    autocompleteService.query(query);
                }
            };

            e.addEventListener('keyup', debounce(onKeyUp, 250) as unknown as EventListener);
        });

        this.rootElement.querySelectorAll('hawksearch-autocomplete').forEach((e) => {
            e.addEventListener('hawksearch:close-autocomplete', (event: Event) => {
                this.toggleAutocomplete(false);
            });
        });
    }

    private toggleAutocomplete(visible: boolean): void {
        this.rootElement.querySelectorAll('hawksearch-autocomplete').forEach((e) => {
            const component = e as AutocompleteComponent;

            component.style.display = visible ? 'block' : 'none';
        });
    }
}
